import { useState, useEffect } from 'react'
import { Container, Form, Button, Card, Alert, InputGroup } from 'react-bootstrap'
import { Link, useNavigate } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { useAuth } from '../contexts/AuthContext'

function Register() {
  const [formData, setFormData] = useState({ name: '', email: '', password: '' })
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(null)
  const [authMethod, setAuthMethod] = useState('email') // 'email' or 'phone'

  // Phone auth state
  const [phoneNumber, setPhoneNumber] = useState('')
  const [phoneName, setPhoneName] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [codeSent, setCodeSent] = useState(false)
  const [countdown, setCountdown] = useState(0)

  const { register, googleLogin, sendPhoneCode, verifyPhoneCode, error } = useAuth()
  const navigate = useNavigate()

  // Countdown timer for code expiration
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000)
      return () => clearTimeout(timer)
    }
  }, [countdown])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setSuccess(null)
    try {
      await register(formData)
      setSuccess('Account created! Please check your email to activate your account.')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleSendCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const result = await sendPhoneCode(phoneNumber)
      setCodeSent(true)
      setCountdown(result.expires_in || 300)
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleVerifyCode = async (e) => {
    e.preventDefault()
    setLoading(true)
    try {
      const { isNewUser } = await verifyPhoneCode(phoneNumber, verificationCode, phoneName || undefined)
      if (isNewUser) {
        setSuccess('Account created successfully!')
      }
      navigate('/search')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true)
    try {
      await googleLogin(credentialResponse.credential)
      navigate('/search')
    } catch (err) {
      // Error handled by context
    } finally {
      setLoading(false)
    }
  }

  const handleGoogleError = () => {
    console.error('Google sign-up failed')
  }

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  return (
    <Container className="mt-5" style={{ maxWidth: '500px' }}>
      <Card>
        <Card.Body>
          <h2 className="text-center mb-4">Create Account</h2>
          {error && <Alert variant="danger">{error}</Alert>}
          {success && <Alert variant="success">{success}</Alert>}

          {/* Google Sign-Up */}
          <div className="d-flex justify-content-center mb-3">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              theme="outline"
              size="large"
              text="signup_with"
              shape="rectangular"
              width="100%"
            />
          </div>

          {/* Auth Method Toggle */}
          <div className="d-flex justify-content-center mb-3">
            <Button
              variant={authMethod === 'email' ? 'primary' : 'outline-primary'}
              size="sm"
              className="me-2"
              onClick={() => setAuthMethod('email')}
            >
              Email
            </Button>
            <Button
              variant={authMethod === 'phone' ? 'primary' : 'outline-primary'}
              size="sm"
              onClick={() => setAuthMethod('phone')}
            >
              Phone
            </Button>
          </div>

          <div className="text-center text-muted mb-3">
            <span>or register with {authMethod}</span>
          </div>

          {authMethod === 'email' ? (
            <Form onSubmit={handleSubmit}>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  disabled={loading}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  disabled={loading}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Password</Form.Label>
                <Form.Control
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  disabled={loading}
                  minLength={8}
                />
                <Form.Text className="text-muted">
                  Must be at least 8 characters.
                </Form.Text>
              </Form.Group>
              <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                {loading ? 'Creating account...' : 'Register'}
              </Button>
            </Form>
          ) : (
            <>
              {!codeSent ? (
                <Form onSubmit={handleSendCode}>
                  <Form.Group className="mb-3">
                    <Form.Label>Name (optional)</Form.Label>
                    <Form.Control
                      type="text"
                      value={phoneName}
                      onChange={(e) => setPhoneName(e.target.value)}
                      disabled={loading}
                      placeholder="Your name"
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      placeholder="+1234567890"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      required
                      disabled={loading}
                    />
                    <Form.Text className="text-muted">
                      Enter in E.164 format (e.g., +14155551234)
                    </Form.Text>
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100" disabled={loading}>
                    {loading ? 'Sending...' : 'Send Verification Code'}
                  </Button>
                </Form>
              ) : (
                <Form onSubmit={handleVerifyCode}>
                  <Form.Group className="mb-3">
                    <Form.Label>Phone Number</Form.Label>
                    <Form.Control
                      type="tel"
                      value={phoneNumber}
                      disabled
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label>Verification Code</Form.Label>
                    <InputGroup>
                      <Form.Control
                        type="text"
                        placeholder="123456"
                        value={verificationCode}
                        onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        required
                        disabled={loading}
                        maxLength={6}
                      />
                      {countdown > 0 && (
                        <InputGroup.Text>{formatTime(countdown)}</InputGroup.Text>
                      )}
                    </InputGroup>
                    <Form.Text className="text-muted">
                      Enter the 6-digit code sent to your phone
                    </Form.Text>
                  </Form.Group>
                  <Button variant="primary" type="submit" className="w-100 mb-2" disabled={loading || verificationCode.length !== 6}>
                    {loading ? 'Verifying...' : 'Verify & Create Account'}
                  </Button>
                  <Button
                    variant="link"
                    className="w-100"
                    onClick={() => {
                      setCodeSent(false)
                      setVerificationCode('')
                    }}
                    disabled={loading}
                  >
                    Change phone number
                  </Button>
                  {countdown === 0 && (
                    <Button
                      variant="outline-secondary"
                      className="w-100 mt-2"
                      onClick={handleSendCode}
                      disabled={loading}
                    >
                      Resend Code
                    </Button>
                  )}
                </Form>
              )}
            </>
          )}

          <div className="text-center mt-3">
            Already have an account? <Link to="/login">Login</Link>
          </div>
        </Card.Body>
      </Card>
    </Container>
  )
}

export default Register
